import type { FunctionComponent } from "react";
import { useCallback, useState } from "react";
import { t } from "i18next";

import {
  StyledIcon,
  StyledStatus,
  StyledTitle,
  StyledWrapper,
  StyledChildren,
} from "./styles";
import { Icon, Text } from "@smartsuite/react-ui/lib";
import { IconColorNames } from "@smartsuite/types";

export type ActivityGroupStatus =
  | "is_success"
  | "is_error"
  | "is_pending"
  | "conditions_not_met"
  | "rejected";

interface ActivityDetailsGroupProps {
  status?: ActivityGroupStatus;
  label: string;
  subLabel?: string;
  children: React.ReactNode;
  onClick?: () => void;
}

const STATUS_LABEL_KEY: Record<ActivityGroupStatus, string> = {
  is_success: "job-list.success",
  is_error: "job-list.error",
  is_pending: "job-list.running",
  conditions_not_met: "job-list.conditions-not-met",
  rejected: "job-list.rejected",
};

export const ActivityDetailsGroup: FunctionComponent<
  ActivityDetailsGroupProps
> = ({ label, subLabel, status, children, onClick }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggle = useCallback(() => {
    setIsExpanded((prev) => !prev);
    onClick?.();
  }, [onClick]);

  return (
    <StyledWrapper>
      <StyledTitle onClick={handleToggle}>
        <StyledIcon isExpanded={isExpanded}>
          <Icon name="arrow-up-1" color={IconColorNames.dark} />
        </StyledIcon>
        <Text size="l" weight="semibold">
          {label}
        </Text>
        {subLabel && <Text size="l">&nbsp;({subLabel})</Text>}
        {status !== undefined && (
          <StyledStatus status={status}>
            <Text color="reverse">{t(STATUS_LABEL_KEY[status])}</Text>
          </StyledStatus>
        )}
      </StyledTitle>
      {isExpanded && <StyledChildren>{children}</StyledChildren>}
    </StyledWrapper>
  );
};
